import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'

import { TEAMS_READERS } from '../auth/roles.ts'
import { guardRoute } from '../auth/guard.ts'
import { parseOnboardingStep, type OnboardingSearch } from '../data/club-onboarding.ts'
import { TEAM_GENDERS, type TeamGenderFilter } from '../data/team-filters.ts'
import { AGE_GROUPS, type AgeGroup } from '../messages/index.ts'
import { rootRoute } from './root.tsx'

// The list of `/teams`. Both filters are typed search params and not state: the filtered view is
// linkable, which is what a dirigente sends to the coach of one category.

export interface TeamsSearch extends OnboardingSearch {
  readonly gender: TeamGenderFilter | null
  readonly ageGroup: AgeGroup | null
}

export function parseGender(value: unknown): TeamGenderFilter | null {
  if (typeof value !== 'string') return null
  return TEAM_GENDERS.includes(value as TeamGenderFilter) ? (value as TeamGenderFilter) : null
}

export function parseAgeGroup(value: unknown): AgeGroup | null {
  if (typeof value !== 'string') return null
  return Object.hasOwn(AGE_GROUPS, value) ? (value as AgeGroup) : null
}

/** Both filters unset. Also what `stripSearchParams` keeps out of the URL. */
const DEFAULT_TEAMS_SEARCH: TeamsSearch = { gender: null, ageGroup: null }

export const teamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/teams',
  validateSearch: (search: Record<string, unknown>): TeamsSearch => ({
    gender: parseGender(search.gender),
    ageGroup: parseAgeGroup(search.ageGroup),
    onboarding: parseOnboardingStep(search.onboarding),
  }),
  search: {
    // Defaults leave the URL: a colleague receives /teams?gender=female, never
    // /teams?gender=null&ageGroup=null.
    middlewares: [stripSearchParams(DEFAULT_TEAMS_SEARCH)],
  },
  beforeLoad: ({ context, location }) =>
    guardRoute({
      queryClient: context.queryClient,
      requiredRoles: TEAMS_READERS,
      currentPath: location.href,
    }),
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../pages/teams.tsx')),
})

// The literal and not `teamsRoute.to`: a link constant is evaluated when the module loads,
// and the route's `to` is still empty then — the link would resolve to the current page.
export const teamListLink = { to: '/teams', search: DEFAULT_TEAMS_SEARCH } as const
